/**
 * What happens to each benefit she receives, if we pay her.
 *
 * THE ONE RULE THIS SCREEN EXISTS TO HOLD
 * `unknown` must never look like `excluded`. A mother told her benefits are
 * safe on an unknown is worse off than one told we do not know — she makes a
 * decision she cannot unmake. So `unknown` gets its own colour, its own icon,
 * its own words, and it sorts to the top with the harmful ones rather than
 * sitting quietly among the excluded.
 *
 * Every row names the regulation behind it, because the moment the answer
 * matters is a redetermination or an appeal, and an answer that cannot cite
 * itself is no use there.
 *
 * Status colour never carries meaning on its own: each treatment ships with an
 * icon and a label as well.
 */

import React, { useMemo } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  ShieldAlert,
  ShieldCheck,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  TREATMENT_LABELS,
  type Assessment,
  type BenefitImpact,
  type Treatment,
} from './types';

interface TreatmentStyle {
  chip: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Lower sorts first. Anything a human must look at comes before the rest. */
  rank: number;
}

const TREATMENT_STYLE: Record<Treatment, TreatmentStyle> = {
  no_route: {
    chip: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-950 dark:text-red-100 dark:border-red-800',
    icon: ShieldAlert,
    rank: 0,
  },
  counted: {
    chip: 'bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-950 dark:text-orange-100 dark:border-orange-800',
    icon: AlertTriangle,
    rank: 1,
  },
  // Deliberately NOT a quiet grey. An unknown is a reason to stop, and it sits
  // with the harmful answers rather than with the safe ones.
  unknown: {
    chip: 'bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-950 dark:text-violet-100 dark:border-violet-800',
    icon: HelpCircle,
    rank: 2,
  },
  counted_with_shield: {
    chip: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-800',
    icon: ShieldCheck,
    rank: 3,
  },
  excluded: {
    chip: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-100 dark:border-emerald-800',
    icon: CheckCircle2,
    rank: 4,
  },
};

const money = (v: string | null) =>
  v == null ? null : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));

export const TreatmentChip: React.FC<{ treatment: Treatment }> = ({ treatment }) => {
  const style = TREATMENT_STYLE[treatment] ?? TREATMENT_STYLE.unknown;
  const Icon = style.icon;
  return (
    <Badge className={`gap-1.5 border ${style.chip}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {TREATMENT_LABELS[treatment] ?? treatment}
    </Badge>
  );
};

const ImpactRow: React.FC<{ impact: BenefitImpact }> = ({ impact }) => {
  const cap = money(impact.period_cap);
  const total = money(impact.period_total);
  const overshoot = money(impact.overshoot);

  return (
    <li className="border-t border-border py-4 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-foreground">{impact.program_name}</p>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {impact.program_code}
          </p>
        </div>
        <TreatmentChip treatment={impact.treatment} />
      </div>

      <p className="mt-2 text-sm text-foreground">{impact.explanation}</p>

      {cap && (
        <p className="mt-2 text-sm tabular-nums text-foreground">
          Excluded up to <strong>{cap}</strong> per {(impact.period_type ?? '').replace(/_/g, ' ')}
          {total && <> · {total} already counted</>}
          {overshoot && Number(impact.overshoot) > 0 && (
            <> · <span className="text-orange-700 dark:text-orange-300">{overshoot} over</span></>
          )}
        </p>
      )}

      {impact.what_changes_it && (
        <p className="mt-2 rounded-md bg-muted/60 p-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">What changes it: </span>
          {impact.what_changes_it}
        </p>
      )}

      <p className="mt-2 text-xs text-muted-foreground">
        {impact.source_url ? (
          <a
            href={impact.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline underline-offset-2"
          >
            {impact.citation}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        ) : (
          impact.citation
        )}
        <span className="ml-2 opacity-60">rule v{impact.rule_version}</span>
      </p>
    </li>
  );
};

interface Props {
  assessment: Assessment | null;
  blockedReason?: string | null;
}

export const BenefitImpactPanel: React.FC<Props> = ({ assessment, blockedReason }) => {
  const ordered = useMemo(() => {
    if (!assessment) return [];
    return [...assessment.impacts].sort(
      (a, b) =>
        (TREATMENT_STYLE[a.treatment]?.rank ?? 9) - (TREATMENT_STYLE[b.treatment]?.rank ?? 9) ||
        a.program_code.localeCompare(b.program_code),
    );
  }, [assessment]);

  if (blockedReason) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Benefit impact</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 rounded-lg border border-violet-300 bg-violet-50 p-3 dark:border-violet-800 dark:bg-violet-950">
            <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-700 dark:text-violet-300" aria-hidden="true" />
            <p className="text-sm text-violet-900 dark:text-violet-100">{blockedReason}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!assessment) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">
            Benefit impact · {assessment.state_code}
          </CardTitle>
          {assessment.requires_counsellor ? (
            <Badge className="gap-1.5 border border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              A counsellor must review this
            </Badge>
          ) : (
            <Badge className="gap-1.5 border border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Nothing here reduces what she receives
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          What the rules on file say, with the authority behind each answer. This is not a
          determination by any agency and it is not advice.
        </p>
      </CardHeader>

      <CardContent>
        {assessment.unmodelled.length > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-violet-300 bg-violet-50 p-3 dark:border-violet-800 dark:bg-violet-950">
            <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-700 dark:text-violet-300" aria-hidden="true" />
            <div className="text-sm text-violet-900 dark:text-violet-100">
              <p className="font-medium">
                We hold no rule for {assessment.unmodelled.join(', ')}.
              </p>
              <p className="mt-1">
                That is not the same as "no effect". A missing rule is the thing most likely to
                hurt her, so treat these as open until someone checks them.
              </p>
            </div>
          </div>
        )}

        {ordered.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No benefits were recorded for this member.
          </p>
        ) : (
          <ul className="space-y-0">
            {ordered.map((impact) => (
              <ImpactRow key={`${impact.program_code}-${impact.rule_version}`} impact={impact} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default BenefitImpactPanel;
